<template>
    <div class="radio">
        <label
            :for="name"
            class="radio__label"
            :class="{ active: inputFocus || filled, disabled, error }"
            >{{ label }}</label
        >
        <div class="radio__wrapper" v-for="(item, i) in items" :key="i">
            <input
                :class="{ strong: strongText }"
                type="radio"
                :name="name"
                :id="item.value"
                :value="item.value"
                @change="updateValue(item)"
                @focus="toggleInputFocus(true)"
                @blur="toggleInputFocus(false)"
                :checked="value && item.value === value.value"
                tabindex="0"
                :disabled="disabled"
            />
            <label
                ref="radio"
                :class="{ error }"
                :for="item.value"
                v-html="item.display"
            ></label>
        </div>
        <InputHint
            v-if="!hideHintError"
            :error="error"
            :hint="hint"
            :required="required"
            :showOptional="showOptional"
        />
    </div>
</template>

<script>
import InputHint from "./InputHint";

export default {
  name: "RadioButtons",
  components: {
    InputHint,
  },
  props: {
    name: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      required: true,
    },
    value: {
      type: Object,
      default: null,
    },
    focus: {
      type: Boolean,
      default: false,
    },
    hideHintError: {
      type: Boolean,
      default: false,
    },
    hint: String,
    error: String,
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showOptional: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      required: true,
    },
    valueField: {
      type: String,
      default: "value",
    },
    displayField: {
      type: String,
      default: "display",
    },
    strongText: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    filled() {
      return this.value !== null;
    },
  },
  data() {
    return {
      inputFocus: this.focus,
    };
  },
  mounted() {
    if (this.focus) this.setFocus();
  },
  methods: {
    setFocus() {
      this.$refs.radio[0].focus();
    },
    toggleInputFocus(toggle) {
      this.inputFocus = toggle;
      this.$emit(toggle ? "focus" : "blur");
    },
    updateValue(item) {
      this.$emit("change", item);
      this.$emit("resolve-error");
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/style/_variables.scss";
.radio {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    position: relative;
    width: 100%;
    margin-bottom: $padding-xl;

    &__label {
        padding-left: $padding-input-left;

        font-size: $font-size-sm;
        color: $label-gray;
        font-weight: 400;
        transition: $transition-simple-fast;
        z-index: 2;
        cursor: pointer;
        margin-bottom: $padding-sm;

        &.active {
            color: $blue-navy;
        }

        &.error {
            color: $red-active;
        }

        &.disabled {
            color: $label-gray;
            cursor: default;
        }
    }

    &__wrapper {
        position: relative;
        display: flex;
        flex-direction: column;
        width: 100%;
        margin-bottom: 2px;

        &:last-child {
            margin-bottom: 0;
        }

        label {
            position: relative;

            font-size: $font-size-md;
            color: $body-gray;
            padding: $padding-md $padding-input-left;
            padding-left: calc(
                #{$padding-input-left} + #{$font-size-md} + #{$padding-md}
            );
            border-radius: $border-radius-md;
            cursor: pointer;
            background-color: transparent;
            transition: $transition-simple-xtra-fast;
            line-height: 1.5;

            &::before {
                content: "";
                position: absolute;
                top: 13px;
                left: $padding-input-left;
                width: $font-size-md;
                height: $font-size-md;
                border-radius: 50%;
                border: 2px solid $body-gray;
                box-sizing: border-box;
                transition: $transition-simple-xtra-fast;
            }

            &.error {
                color: $red-active;
                background-color: $red-xtra-light;

                &::before {
                    border-color: $red-active;
                }
            }
        }
        .strong {
            &:checked {
                & + label {
                    font-weight: bold;
                }
            }
        }
        input {
            opacity: 0;
            position: absolute;
            top: 0;

            &:focus {
                & + label {
                    color: $blue-navy;
                    background-color: $blue-gray-light;

                    &::before {
                        border-color: $blue-navy;
                    }
                }
            }

            &:checked {
                & + label {
                    color: $blue-navy;

                    &::before {
                        border-color: $blue-navy;
                        background-color: $blue-navy;
                    }
                }
            }

            &:disabled {
                & + label {
                    color: $label-gray;
                    cursor: default;

                    &::before {
                        border-color: $background-dark;
                        background-color: $background-dark;
                    }
                }

                &:checked {
                    & + label {
                        color: $blue-gray-dark;

                        &::before {
                            border-color: $blue-gray-active;
                            background-color: $blue-gray-active;
                        }
                    }
                }
            }
        }
    }
}
</style>
